import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {CardIncomeOutcome} from '@gatewee/types/system';
import {
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
);

@Component({
  selector: 'gatewee-income-card',
  templateUrl: './income-card.component.html',
  standalone: false,
})
export class IncomeCardComponent implements OnChanges, AfterViewInit, OnInit {
  @Input() data: CardIncomeOutcome = {type: 'Income', items: []};

  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;
  private chartInstance!: Chart;

  lastMonth: {amount: number; change: number} = {amount: 0, change: 0};

  ngOnInit(): void {
    this.lastMonth = {
      amount: this.data.items[this.data.items.length - 1]?.amount || 0,
      change: this.calculateChange(),
    };
  }

  ngAfterViewInit(): void {
    if (this.data.items.length > 0) {
      this.initializeChart();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.chartCanvas) {
      this.lastMonth = {
        amount: this.data.items[this.data.items.length - 1]?.amount || 0,
        change: this.calculateChange(),
      };
      if (this.chartInstance) {
        this.updateChart();
      } else {
        this.initializeChart();
      }
    }
  }

  private initializeChart(): void {
    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (ctx && this.data.items.length > 0) {
      this.chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.data.items.map(item => item.label),
          datasets: [
            {
              label: this.data.type,
              backgroundColor: ['rgba(234, 73, 137, 0)'],
              borderColor: '#1EAAE7',
              pointBackgroundColor: '#1EAAE7',
              pointBorderColor: '#1EAAE7',
              borderWidth: 4,
              pointHoverBackgroundColor: '#1EAAE7',
              pointHoverBorderColor: '#1EAAE7',
              tension: 0.5,
              data: this.data.items.map(item => item.amount),
            },
          ],
        },
        options: {
          plugins: {
            legend: {display: false},
            tooltip: {
              intersect: false,
              padding: 10,
              caretPadding: 10,
              mode: 'nearest',
            },
          },
          interaction: {
            mode: 'nearest',
            intersect: false,
          },
          responsive: true,
          maintainAspectRatio: false,
          hover: {
            mode: 'index',
          },
          scales: {
            x: {
              display: false,
              grid: {display: false},
              title: {
                display: true,
                text: 'Month',
              },
              ticks: {
                maxTicksLimit: 11,
                autoSkip: true,
              },
            },
            y: {
              display: false,
              grid: {display: false},
              title: {
                display: true,
                text: 'Value',
              },
              beginAtZero: true,
              ticks: {
                padding: 0,
                maxTicksLimit: 11,
              },
            },
          },
          elements: {
            point: {
              radius: 0,
              borderWidth: 0,
            },
          },
          layout: {
            padding: {
              left: 0,
              right: 0,
              top: 5,
              bottom: 20,
            },
          },
        },
      });
    }
  }

  private updateChart(): void {
    if (this.chartInstance) {
      this.chartInstance.data.labels = this.data.items.map(item => item.label);
      this.chartInstance.data.datasets[0].data = this.data.items.map(
        item => item.amount,
      );
      this.chartInstance.update();
    }
  }

  private calculateChange(): number {
    if (this.data.items.length < 2) return 0;
    const lastMonthAmount = this.data.items[this.data.items.length - 1].amount;
    const previousMonthAmount =
      this.data.items[this.data.items.length - 2].amount;
    const change =
      ((lastMonthAmount - previousMonthAmount) / previousMonthAmount) * 100;
    return parseFloat(change.toFixed(2));
  }
}
